import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef, ChangeEvent } from 'react';
import { Select, Button } from 'common/components';
import { DecisionNodeData, DecisionFieldType, NodeData, DecisionConditionFormRow } from '../../WorkflowBuilder.types';
import { Node } from '@xyflow/react';
import { Disposition, TerminalCallType } from 'types/call.type';
import { PartyStages } from 'services/api/getPartyStages';
import { SMSNodeStatus } from 'types/sms.type';
import { useGetPartyStages } from 'services/hooks/queries/useGetPartyStages';
import { MinusIcon, PlusIcon } from 'lucide-react';
type NextNodeType = 'CALL' | 'SMS' | 'DELAY' | 'END';

function getNextNodeTypeFromNodeType(nodeType: string | undefined): NextNodeType | null {
    if (nodeType === 'callNode') return 'CALL';
    if (nodeType === 'smsNode' || nodeType === 'emailNode') return 'SMS';
    if (nodeType === 'delayNode') return 'DELAY';
    if (nodeType === 'endNode') return 'END';
    return null;
}

function getNextNodeTypeForId(nodeId: string, nodeList: Node<NodeData>[]): NextNodeType | null {
    const n = nodeList.find((node) => node.id === nodeId);
    return getNextNodeTypeFromNodeType(n?.type);
}

const DECISION_FIELD_OPTIONS: { value: DecisionFieldType; label: string }[] = [
    { value: 'call_disposition', label: 'Call disposition' },
    { value: 'call_status', label: 'Call status' },
    { value: 'party_stage', label: 'Party stage' },
    { value: 'sms_status', label: 'SMS status' },
];

const NEXT_NODE_TYPE_OPTIONS: { value: NextNodeType; label: string }[] = [
    { value: 'CALL', label: 'Call' },
    { value: 'SMS', label: 'SMS' },
    { value: 'DELAY', label: 'Delay' },
    { value: 'END', label: 'End' },
];

const NEXT_NODE_TERMINAL_TYPE_OPTIONS: { value: NextNodeType; label: string }[] = [
    { value: 'SMS', label: 'SMS' },
    { value: 'END', label: 'End' },
];

const DEFAULT_NEXT_OPTIONS: { value: string; label: string }[] = [
    { value: '', label: 'None' },
    ...NEXT_NODE_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
];

const DISPOSITION_OPTIONS = Object.entries(Disposition).map(([value]) => ({
    value,
    label: value
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase()),
}));

const CALL_STATUS_OPTIONS = Object.entries(TerminalCallType).map(([value]) => ({
    value,
    label: value
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase()),
}));

const PARTY_STAGE_OPTIONS = Object.keys(PartyStages).map((key) => ({
    value: key,
    label: PartyStages[key as keyof typeof PartyStages],
}));

const SMS_STATUS_OPTIONS = Object.entries(SMSNodeStatus).map(([key, value]) => ({
    value: value as string,
    label: key,
}));

export interface NodeConfigDecisionRef {
    getData: () => {
        conditionRows: DecisionConditionFormRow[];
        defaultNextNodeType: NextNodeType | null;
    };
}

interface NodeConfigDecisionProps {
    node: Node<DecisionNodeData>;
    nodes?: Node<NodeData>[];
    terminalDecisionNodeId?: string | null;
}

function valueOptionsForField(
    decisionField: DecisionFieldType,
    partyStages: ReturnType<typeof useGetPartyStages>['partyStages']
): { value: string; label: string }[] {
    if (decisionField === 'call_disposition') return DISPOSITION_OPTIONS;
    if (decisionField === 'call_status') return CALL_STATUS_OPTIONS;
    if (decisionField === 'party_stage') {
        return partyStages?.data?.length
            ? partyStages.data.map((key) => ({ value: key, label: PartyStages[key] || key }))
            : PARTY_STAGE_OPTIONS;
    }
    return SMS_STATUS_OPTIONS;
}

export const NodeConfigDecision = forwardRef<NodeConfigDecisionRef, NodeConfigDecisionProps>(
    ({ node, nodes = [], terminalDecisionNodeId }, ref) => {
        const { partyStages } = useGetPartyStages();
        const isTerminalDecision = Boolean(terminalDecisionNodeId && node.id === terminalDecisionNodeId);

        const [conditionRows, setConditionRows] = useState<DecisionConditionFormRow[]>(() => {
            const conditions = node.data?.conditions;
            if (conditions && conditions.length > 0) {
                return conditions.map((c) => ({
                    decisionField: c.decisionField,
                    values: [...c.values],
                    nextNodeType:
                        (nodes.length > 0 && c.nextNode ? getNextNodeTypeForId(c.nextNode, nodes) : null) ?? 'CALL',
                }));
            }
            return [{ decisionField: 'call_disposition', values: [], nextNodeType: 'CALL' }];
        });

        const [defaultNextNodeType, setDefaultNextNodeType] = useState<NextNodeType | null>(() => {
            const dn = node.data?.defaultNextNode;
            if (!dn || !nodes.length) return null;
            return getNextNodeTypeForId(dn, nodes);
        });

        const dataRef = useRef({ conditionRows, defaultNextNodeType });
        dataRef.current = { conditionRows, defaultNextNodeType };

        useImperativeHandle(ref, () => ({
            getData: () => ({
                conditionRows: dataRef.current.conditionRows,
                defaultNextNodeType: dataRef.current.defaultNextNodeType,
            }),
        }));

        useEffect(() => {
            const conditions = node.data?.conditions;
            if (conditions && conditions.length > 0) {
                setConditionRows(
                    conditions.map((c) => ({
                        decisionField: c.decisionField,
                        values: [...c.values],
                        nextNodeType:
                            (nodes.length > 0 && c.nextNode ? getNextNodeTypeForId(c.nextNode, nodes) : null) ?? 'CALL',
                    }))
                );
            }
            const dn = node.data?.defaultNextNode;
            if (dn && nodes.length) {
                setDefaultNextNodeType(getNextNodeTypeForId(dn, nodes));
            } else if (!dn) {
                setDefaultNextNodeType(null);
            }
        }, [node.id, node.data?.conditions, node.data?.defaultNextNode, nodes, nodes.length]);

        const addRow = () => {
            setConditionRows((prev) => [
                ...prev,
                { decisionField: 'call_disposition', values: [], nextNodeType: 'CALL' },
            ]);
        };

        const removeRow = (index: number) => {
            setConditionRows((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
        };

        const updateRowField = (index: number, field: keyof DecisionConditionFormRow, value: unknown) => {
            setConditionRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
        };

        return (
            <div className='flex flex-col gap-xl'>
                <div className='h-1 w-full bg-gray-400' />
                <div className='flex flex-col gap-lg'>
                    <div className='flex justify-between items-center'>
                        <h4 className='font-semibold'>Conditions</h4>
                        <Button type='button' variant='secondary' onClick={addRow} icon={PlusIcon}>
                            Add condition
                        </Button>
                    </div>
                    {conditionRows.map((row, index) => {
                        const valueOpts = valueOptionsForField(row.decisionField, partyStages);

                        const chosenValuesOtherRows = conditionRows
                            .filter((_, i) => i !== index)
                            .flatMap((r) => r.values);
                        const optionsWithDisable = valueOpts.map((opt) => ({
                            ...opt,
                            isDisabled: chosenValuesOtherRows.includes(opt.value) && !row.values.includes(opt.value),
                        }));

                        return (
                            <div
                                key={index}
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 2fr 1fr 36px',
                                    gap: 'var(--spacing-md)',
                                }}
                            >
                                <Select
                                    id={`condition-field-${index}`}
                                    label='Field'
                                    value={row.decisionField}
                                    onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                                        updateRowField(index, 'decisionField', e.target.value as DecisionFieldType);
                                        updateRowField(index, 'values', []);
                                    }}
                                    options={DECISION_FIELD_OPTIONS}
                                />

                                <Select
                                    id={`values-${index}`}
                                    label='Values'
                                    isMulti
                                    options={optionsWithDisable}
                                    value={row.values}
                                    onChange={(vals) => updateRowField(index, 'values', vals)}
                                    placeholder='Select one or more values'
                                    styles={{ container: (base) => ({ ...base, flexBasis: '100%' }) }}
                                />

                                <Select
                                    id={`condition-next-${index}`}
                                    label='Next node type'
                                    value={row.nextNodeType}
                                    onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                                        updateRowField(index, 'nextNodeType', e.target.value as NextNodeType)
                                    }
                                    options={
                                        isTerminalDecision ? NEXT_NODE_TERMINAL_TYPE_OPTIONS : NEXT_NODE_TYPE_OPTIONS
                                    }
                                />
                                <div style={{ marginTop: '26px' }}>
                                    <Button
                                        type='button'
                                        variant='secondary'
                                        onClick={() => removeRow(index)}
                                        disabled={conditionRows.length <= 1}
                                        icon={MinusIcon}
                                        iconOnly
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
                {!isTerminalDecision && (
                    <div className='flex flex-col gap-md mt-2xl'>
                        <Select
                            id='default-next-type'
                            label='Default next node (optional)'
                            value={defaultNextNodeType ?? ''}
                            onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                                const v = e.target.value;
                                setDefaultNextNodeType(v === '' ? null : (v as NextNodeType));
                            }}
                            options={DEFAULT_NEXT_OPTIONS}
                        />
                    </div>
                )}
            </div>
        );
    }
);

NodeConfigDecision.displayName = 'NodeConfigDecision';
